/**
 * A number that tweens to its new value (~600ms ease-out, UX-SPEC §6).
 * Reduced motion: jumps straight to the value. Negatives use a real minus
 * sign (U+2212), never an ASCII hyphen.
 */
import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '../game/interactions';
import { formatDelta, formatScore } from '../lib/format';

interface AnimatedNumberProps {
  value: number;
  duration?: number;
  className?: string;
  /** Prefix positive values with "+" (score floats). */
  signed?: boolean;
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export function AnimatedNumber({ value, duration = 600, className, signed = false }: AnimatedNumberProps) {
  const [shown, setShown] = useState(value);
  const shownRef = useRef(value);
  const frame = useRef(0);

  useEffect(() => {
    const from = shownRef.current;
    if (from === value) return;
    if (prefersReducedMotion() || typeof requestAnimationFrame !== 'function') {
      shownRef.current = value;
      setShown(value);
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const next = Math.round(from + (value - from) * easeOutCubic(t));
      shownRef.current = next;
      setShown(next);
      if (t < 1) frame.current = requestAnimationFrame(step);
    };
    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [value, duration]);

  return (
    <span className={className} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {signed ? formatDelta(shown) : formatScore(shown)}
    </span>
  );
}
